import React, { useState } from 'react';
import { BookOpen, Building2, CalendarDays, Check, CheckCircle2, Clock3, Compass, GraduationCap, Server } from 'lucide-react';
import CalrizLogo from '../../assets/logos/logo-oficial-calriz.png';
import GoogleEducationLogo from '../../assets/logos/google-education-horizontal.webp';
import GeminiLogo from '../../assets/logos/gemini-evento-trim.png';
import HeroImg from '../../assets/convite-evento/ia-pratica-pedagogica.png';
import './ConviteEventoIA.css';
import Seo from '../../seo/Seo';

const REGISTRATION_URL = 'https://backend.calriz.com/api/registro-evento';

const topics = [
  { text: 'IA no planejamento docente', color: 'blue' },
  { text: 'Decisões para uma adoção responsável', color: 'red' },
  { text: 'Dados para compreender a aprendizagem', color: 'yellow' },
  { text: 'Do diagnóstico à intervenção', color: 'green' },
  { text: 'Formação e acompanhamento', color: 'blue' },
];

const audiences = [
  {
    Icon: Building2, color: 'blue', title: 'Para quem lidera a educação',
    items: ['Secretários e dirigentes de Educação', 'Gestores de redes e instituições públicas', 'Reitores e pró-reitores', 'Coordenadores pedagógicos', 'Lideranças de inovação e tecnologia educacional'],
  },
  {
    Icon: GraduationCap, color: 'green', title: 'Para quem faz o ensino acontecer',
    items: ['Professores e formadores', 'Equipes pedagógicas', 'Equipes de tecnologia', 'Equipes de dados'],
  },
];

const journey = [
  { Icon: Server, color: 'blue', label: 'Infraestrutura', text: 'A base tecnológica que sustenta a rede.' },
  { Icon: BookOpen, color: 'yellow', label: 'Formação', text: 'Educadores e equipes preparados para usar a IA.' },
  { Icon: Compass, color: 'green', label: 'Acompanhamento', text: 'Adoção acompanhada da decisão à sala de aula.' },
];

// const speakers = [
//   { name: 'Mariana Peixoto', initials: 'MP' },
//   { name: 'Thiago Guimarães', initials: 'TG' },
// ];

const states = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

type FormData = { nome: string; email: string; telefone: string; cargo: string; instituicao: string; estado: string; consentimento: boolean };
const initialForm: FormData = { nome: '', email: '', telefone: '', cargo: '', instituicao: '', estado: '', consentimento: false };

// function Speakers() {
//   return <div className="ia-speakers"><span className="ia-speakers-label">Com</span>
//     <ul>{speakers.map(({ name, initials }) => <li key={name}><i aria-hidden="true">{initials}</i>{name}</li>)}</ul>
//   </div>;
// }

function EventDetails() {
  return <div className="ia-event-details">
    <div><span className="ia-detail-icon"><CalendarDays aria-hidden="true" /></span><strong>18 de novembro de 2026<small>Quarta-feira</small></strong></div>
    <div><span className="ia-detail-icon"><Clock3 aria-hidden="true" /></span><strong>Das 19h às 20h<small>Horário de Brasília</small></strong></div>
  </div>;
}

export default function ConviteEventoIA() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'geral', string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field: keyof FormData, value: string | boolean) => {
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: undefined, geral: undefined }));
  };

  const scrollToForm = (event: React.MouseEvent) => {
    event.preventDefault();
    document.getElementById('inscricao-ia')?.scrollIntoView({ behavior: 'smooth' });
  };

  const validate = () => {
    const next: typeof errors = {};
    if (!form.nome.trim()) next.nome = 'Informe seu nome completo.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) next.email = 'Informe um e-mail válido.';
    if (form.telefone.replace(/\D/g, '').length < 10) next.telefone = 'Informe um telefone válido com DDD.';
    if (!form.cargo.trim()) next.cargo = 'Informe seu cargo.';
    if (!form.instituicao.trim()) next.instituicao = 'Informe sua instituição.';
    if (!form.estado) next.estado = 'Selecione seu estado.';
    if (!form.consentimento) next.consentimento = 'É necessário autorizar o uso dos dados para concluir.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const response = await fetch(REGISTRATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(), nome: form.nome.trim(), organizacao: form.instituicao.trim(),
          cargo: form.cargo.trim(), telefone: form.telefone, cidade: "ia_event", estado: form.estado,
          consentimento: form.consentimento,
        }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setSubmitted(true);
      setForm(initialForm);
    } catch (error) {
      console.error('Erro ao enviar inscrição:', error);
      setErrors({ geral: 'Não foi possível enviar sua inscrição. Tente novamente em instantes.' });
    } finally { setSubmitting(false); }
  }

  const fields: { key: keyof FormData; label: string; type?: string; placeholder: string }[] = [
    { key: 'nome', label: 'Nome completo', placeholder: 'Digite seu nome' },
    { key: 'email', label: 'E-mail corporativo', type: 'email', placeholder: 'nome@instituicao.gov.br' },
    { key: 'telefone', label: 'Telefone', type: 'tel', placeholder: '(00) 00000-0000' },
    { key: 'cargo', label: 'Cargo', placeholder: 'Seu cargo' },
    { key: 'instituicao', label: 'Instituição', placeholder: 'Nome da instituição' },
  ];

  return <main className="ia-page">
    <Seo title="IA na prática pedagógica: da gestão à sala de aula" description="Neste Dia dos Professores, descubra como redes e instituições públicas podem usar a inteligência artificial para apoiar quem ensina e fortalecer a aprendizagem." path="/ia-na-pratica-pedagogica" noindex />
    <nav className="ia-nav" aria-label="Navegação do evento"><div className="ia-nav-inner">
      <img src={CalrizLogo} alt="Calriz" className="ia-calriz-logo" />
      <div className="ia-partners"><span>Em parceria com</span><img className="ia-google-education-logo" src={GoogleEducationLogo} alt="Google for Education" /><span className="ia-logo-divider" aria-hidden="true">|</span><img className="ia-gemini-logo" src={GeminiLogo} alt="Gemini" /></div>
    </div></nav>

    <section className="ia-hero"><div className="ia-hero-inner">
      <div className="ia-hero-copy">
        <span className="ia-eyebrow"><i className="ia-dots" /> Especial Dia dos Professores</span>
        <h1>IA na prática pedagógica: da gestão à sala de aula</h1>
        <p className="ia-hero-lead">Neste Dia dos Professores, descubra como redes e instituições públicas podem usar a inteligência artificial para apoiar quem ensina e fortalecer a aprendizagem.</p>
        <p className="ia-hero-support">Um encontro sobre aplicações práticas, formação e decisões de gestão para colocar a IA a serviço dos educadores, com estratégia e responsabilidade.</p>
        <EventDetails />
        <div className="ia-hero-meta">
          <span className="ia-tag">Evento online e gratuito</span>
        </div>
        <a href="#inscricao-ia" onClick={scrollToForm} className="ia-button">Quero participar <span aria-hidden="true">→</span></a>
      </div>
      <div className="ia-hero-art"><img src={HeroImg} alt="Inteligência artificial conectando gestão, dados e aprendizagem" /><span className="ia-art-caption"><i className="ia-caption-gemini" aria-hidden="true">✦</i><b>IA, dados e aprendizagem<br />conectados</b></span></div>
    </div></section>

    <section className="ia-reality"><div className="ia-container">
      <div className="ia-reality-heading"><span className="ia-section-kicker">Contexto</span><h2>Valorizar quem ensina também é oferecer condições para ensinar melhor.</h2></div>
      <div className="ia-reality-content">
        <p>Planejar aulas, adaptar atividades e acompanhar a aprendizagem são partes essenciais do trabalho docente.</p>
        <div className="ia-question"><span>?</span><strong>Como a inteligência artificial pode apoiar essas tarefas, respeitando a autonomia e o conhecimento dos professores?</strong></div>
        <p>A IA já faz parte da realidade educacional, muitas vezes de forma informal ou isolada. Neste Dia dos Professores, a Calriz convida você a discutir como transformar esse uso em apoio concreto, com orientação, formação e decisões conectadas aos objetivos da instituição.</p>
      </div>
    </div></section>

    <section className="ia-topics"><div className="ia-container">
      <span className="ia-section-kicker">O que você verá</span><h2>Da gestão à sala de aula, caminhos para apoiar os educadores</h2>
      <div className="ia-topic-grid">{topics.map(({ text, color }, index) => <article className={`ia-topic-card ia-topic-${color}`} key={text}><span className="ia-topic-number">0{index + 1}</span><h3>{text}</h3></article>)}</div>
    </div></section>

    <section className="ia-audience"><div className="ia-container">
      <div className="ia-audience-heading"><span className="ia-section-kicker">Para quem é este encontro</span><h2>Para quem lidera a educação e para quem faz o ensino acontecer.</h2></div>
      <div className="ia-audience-grid">{audiences.map(({ Icon, color, title, items }) => <article className={`ia-audience-card ia-topic-${color}`} key={title}>
        <span className="ia-audience-icon"><Icon aria-hidden="true" /></span>
        <h3>{title}</h3>
        <ul>{items.map(item => <li key={item}><Check aria-hidden="true" />{item}</li>)}</ul>
      </article>)}</div>
    </div></section>

    <section className="ia-journey"><div className="ia-container">
      <div className="ia-journey-heading">
        <span className="ia-section-kicker">A Calriz</span>
        <h2>Da infraestrutura à adoção: uma jornada conectada à educação.</h2>
        <p>A Calriz conecta tecnologia, formação e acompanhamento para apoiar redes e instituições públicas em sua transformação educacional. Neste encontro, vamos discutir como essa jornada pode colocar a IA a serviço da gestão, dos professores e da aprendizagem.</p>
      </div>
      <ol className="ia-journey-steps">{journey.map(({ Icon, color, label, text }, index) => <li className={`ia-journey-step ia-journey-${color}`} key={label}>
        <span className="ia-journey-icon"><Icon aria-hidden="true" /></span>
        <b>0{index + 1} · {label}</b>
        <small>{text}</small>
      </li>)}</ol>
    </div></section>

    <section className="ia-registration" id="inscricao-ia"><div className="ia-registration-inner">
      <div className="ia-registration-copy"><span className="ia-section-kicker">Especial Dia dos Professores</span><h2>Neste Dia dos Professores, participe dessa conversa.</h2>
        <p>Conheça caminhos para apoiar os educadores com inteligência artificial e construir uma adoção alinhada à realidade da sua instituição.</p>
        <div className="ia-date-card"><strong><small>NOV</small>18</strong><span><b>Quarta-feira, das 19h às 20h</b>Evento online e gratuito</span></div>
      </div>
      <div className={`ia-form-card${submitted ? ' ia-form-card--success' : ''}`}>
        {submitted ? <div className="ia-success" role="status"><CheckCircle2 /><h3>Inscrição realizada!</h3><p>Obrigado por se inscrever. Em breve, você receberá as informações do evento por e-mail.</p></div> :
        <form onSubmit={handleSubmit} noValidate><div className="ia-form-heading"><span className="ia-section-kicker">Inscrição gratuita</span><h3>Inscreva-se gratuitamente</h3><p className="ia-form-subtitle">Preencha os dados abaixo para receber o acesso ao webinar.</p></div>
          {fields.map(field => <div className="ia-field" key={field.key}><label htmlFor={`ia-${field.key}`}>{field.label}</label><input id={`ia-${field.key}`} type={field.type || 'text'} placeholder={field.placeholder} value={String(form[field.key])} onChange={e => update(field.key, e.target.value)} aria-invalid={Boolean(errors[field.key])} maxLength={100} />{errors[field.key] && <small>{errors[field.key]}</small>}</div>)}
          <div className="ia-field"><label htmlFor="ia-estado">Estado</label><select id="ia-estado" value={form.estado} onChange={e => update('estado', e.target.value)} aria-invalid={Boolean(errors.estado)}><option value="">Selecione</option>{states.map(state => <option key={state}>{state}</option>)}</select>{errors.estado && <small>{errors.estado}</small>}</div>
          <label className="ia-consent"><input type="checkbox" checked={form.consentimento} onChange={e => update('consentimento', e.target.checked)} /><span>Autorizo o uso dos meus dados para contato sobre este evento e outras iniciativas da Calriz. Consulte a <a href="/politica-de-privacidade" target="_blank" rel="noreferrer">Política de Privacidade</a>.</span></label>
          {errors.consentimento && <small className="ia-consent-error">{errors.consentimento}</small>}{errors.geral && <p className="ia-form-error" role="alert">{errors.geral}</p>}
          <button className="ia-button ia-submit" disabled={submitting}>{submitting ? 'Enviando...' : <>Quero me inscrever <span aria-hidden="true">→</span></>}</button>
        </form>}
      </div>
    </div></section>
  </main>;
}
